/**
 * Pure utility functions for speaker-interval extraction, face-detection
 * bucketing, and dominant-face selection.
 *
 * These are intentionally stateless and free of editor / store dependencies
 * so they can be unit-tested in isolation.
 */

import type { FaceDetection, SpeakerInterval } from './types';

// ---------------------------------------------------------------------------
// Speaker-interval extraction
// ---------------------------------------------------------------------------

/** Minimal word shape needed to build speaker intervals. */
export type TranscriptWord = {
  ts?: number | null;
  end_ts?: number | null;
  speaker_label?: string;
  type?: string;
};

/** Maximum gap (seconds) between two words of the same speaker before a new interval starts. */
const SAME_SPEAKER_GAP_TOLERANCE = 0.5;

/**
 * Merge adjacent transcript words that share the same speaker into continuous
 * `SpeakerInterval`s.  Words with missing timestamps or non-text type are
 * skipped.  A new interval starts when the speaker changes **or** the gap
 * exceeds `SAME_SPEAKER_GAP_TOLERANCE`.
 */
export function extractSpeakerIntervals(
  transcriptElements: ReadonlyArray<TranscriptWord>,
): SpeakerInterval[] {
  const intervals: SpeakerInterval[] = [];
  let current: SpeakerInterval | null = null;

  for (const el of transcriptElements) {
    if (el.type !== 'text' || el.ts == null || el.end_ts == null) continue;

    const label = el.speaker_label || 'unknown';

    if (
      current &&
      current.speakerLabel === label &&
      el.ts <= current.endTime + SAME_SPEAKER_GAP_TOLERANCE
    ) {
      current.endTime = Math.max(current.endTime, el.end_ts);
    } else {
      if (current) intervals.push(current);
      current = { speakerLabel: label, startTime: el.ts, endTime: el.end_ts };
    }
  }
  if (current) intervals.push(current);

  return intervals;
}

// ---------------------------------------------------------------------------
// Face-detection helpers
// ---------------------------------------------------------------------------

/**
 * Binary-search for the index of the first detection whose `time >= target`.
 * Returns `detections.length` when every detection is earlier.
 */
export function findFirstDetectionIndex(
  detections: readonly FaceDetection[],
  target: number,
): number {
  let lo = 0;
  let hi = detections.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (detections[mid].time < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Return detections whose `time` falls within `[start, end)`.
 * Assumes `detections` is sorted by `time` ascending.
 */
export function sliceDetectionsByTimeRange(
  detections: readonly FaceDetection[],
  start: number,
  end: number,
): FaceDetection[] {
  const startIdx = findFirstDetectionIndex(detections, start);
  const result: FaceDetection[] = [];
  for (let i = startIdx; i < detections.length; i++) {
    if (detections[i].time >= end) break;
    result.push(detections[i]);
  }
  return result;
}

/**
 * Among the given detections, find the `label` (faceId) that appears most
 * frequently.  Tie-break: highest total confidence, then earliest detection.
 *
 * Returns `null` when `detections` is empty.
 */
export function mostFrequentFaceLabel(
  detections: readonly FaceDetection[],
): string | null {
  if (detections.length === 0) return null;

  const groups = new Map<
    string,
    { count: number; totalConfidence: number; earliest: number }
  >();

  for (const d of detections) {
    const g = groups.get(d.label);
    if (g) {
      g.count++;
      g.totalConfidence += d.confidence;
      if (d.time < g.earliest) g.earliest = d.time;
    } else {
      groups.set(d.label, {
        count: 1,
        totalConfidence: d.confidence,
        earliest: d.time,
      });
    }
  }

  let bestLabel: string | null = null;
  let bestCount = -1;
  let bestConf = -1;
  let bestEarliest = Infinity;

  for (const [label, g] of groups) {
    if (
      g.count > bestCount ||
      (g.count === bestCount && g.totalConfidence > bestConf) ||
      (g.count === bestCount &&
        g.totalConfidence === bestConf &&
        g.earliest < bestEarliest)
    ) {
      bestLabel = label;
      bestCount = g.count;
      bestConf = g.totalConfidence;
      bestEarliest = g.earliest;
    }
  }

  return bestLabel;
}

/**
 * Filter `detections` to only those with the given `label`.
 */
export function filterDetectionsByLabel(
  detections: readonly FaceDetection[],
  label: string,
): FaceDetection[] {
  return detections.filter(d => d.label === label);
}
